import { CommonModule, NgFor } from '@angular/common';
import {
    Component,
    EventEmitter,
    Input,
    InputSignal,
    OnDestroy,
    OnInit,
    Output,
    Signal,
    computed,
    effect,
    inject,
    input,
    output,
    untracked,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { DatiqSelectModule } from '@dat/datiq-components-pkg/select';
import { IqControlCheckboxControlComponent } from '@dat/iq-control-checkbox';
import { IqControlTabTabsComponent } from '@dat/iq-control-tab';
import {
    IqControlTextCurrencyComponent,
    IqControlTextGeoComponent,
    IqControlTextIntegerComponent,
    IqControlTextPercentComponent,
    IqControlTextTextComponent,
} from '@dat/iq-control-text';
import { IqEventBusBrokerService } from '@dat/iq-event-bus';
import {
    IqCssClassNamesRule,
    IqCssClassProcessorDirective,
    IqCssClassnames,
    IqStyleProcessorShowElementComponent,
} from '@dat/iq-style-processor';
import { noop } from 'lodash';
import { ControlLegacySelectPipe } from '../control-legacy-select/control-legacy-select.pipe';
import { ControlLegacyTabsPipe } from '../control-legacy-tabs/control-legacy-tabs.pipe';
import { IqControlsActionButtonComponent } from '../iq-controls-action-button/iq-controls-action-button.component';
import { IqControlsControlPanelWrapperComponent } from '../iq-controls-control-panel-wrapper/iq-controls-control-panel-wrapper.component';
import { IqControlsLabelComponent } from '../iq-controls-label/iq-controls-label.component';
import { IqControlsResetButtonComponent } from '../iq-controls-reset-button/iq-controls-reset-button.component';
import { IqControlsSubmitButtonComponent } from '../iq-controls-submit-button/iq-controls-submit-button.component';
import { Control } from '../models';
import { IqControlsStatusService } from '../state';
import { IqControlsActionService } from '../state/services/iq-controls-action.service';
import { IqControlsRulesService } from '../state/services/iq-controls-rules.service';

@Component({
    selector: 'iq-controls-dashboard',
    standalone: true,
    imports: [
        CommonModule,
        NgFor,
        ControlLegacySelectPipe,
        ControlLegacyTabsPipe,
        IqControlsLabelComponent,
        IqControlTextTextComponent,
        IqControlsActionButtonComponent,
        DatiqSelectModule,
        IqStyleProcessorShowElementComponent,
        IqCssClassProcessorDirective,
        IqControlTextPercentComponent,
        IqControlsResetButtonComponent,
        IqControlTextGeoComponent,
        IqControlsSubmitButtonComponent,
        IqControlTextIntegerComponent,
        IqControlCheckboxControlComponent,
        IqControlsControlPanelWrapperComponent,
        IqControlTabTabsComponent,
        IqControlTextCurrencyComponent,
    ],
    templateUrl: './iq-controls-dashboard.component.html',
    styleUrl: './iq-controls-dashboard.component.scss',
})
export class IqControlsDashboardComponent implements OnInit {
    @Input()
    id: string;
    @Input() dashboardId: string;

    controls: InputSignal<Control[]> = input([]);

    currentControls: Signal<Control[]> = computed(() => {
        return this._status.getControls(this.id)();
    });

    containerClasses: InputSignal<IqCssClassnames> = input({});
    cssClassRules: InputSignal<IqCssClassNamesRule[]> = input([]);

    @Output()
    clicked = new EventEmitter<any>();

    @Output()
    changed = new EventEmitter<any>();

    @Output()
    keyupped = new EventEmitter<any>();

    @Output()
    submitted = new EventEmitter<any>();

    @Output()
    searching = new EventEmitter<any>();

    opened = output();

    closed = output();

    @Input()
    fg: FormGroup = new FormGroup({});

    @Output()
    resetted = new EventEmitter<any>();

    private readonly _status: IqControlsStatusService = inject(
        IqControlsStatusService,
    );

    private readonly _action: IqControlsActionService = inject(
        IqControlsActionService,
    );

    private readonly _controlRules = inject(IqControlsRulesService);
    private _events: IqEventBusBrokerService = inject(IqEventBusBrokerService);
    fg$: any;

    constructor() {
        this._action.register();
        this._controlRules.register();

        const initializeEffect = effect(() => {
            const propControls = this.controls();

            untracked(() => {
                this._status.replaceControls(this.id, propControls);
                initializeEffect.destroy();
            });
        });
    }

    ngOnInit() {
        this._status.addAllControls(this.id, this.controls());
        this._status.registerFormGroup(this.id, this.fg);
    }

    click(...args): void {
        const emitData = this.createEmitData(args);

        this.clicked.emit(emitData);
    }

    change(...args) {
        const emitData = this.createEmitData(args);
        /**
         * This dispatches an event indicating the section was opened or closed
         *
         *
         * To listen to these events, you can use the following format in your Dashboard, Section, or Component:
         *
         * ```json
         * {
         *  "onChange": [
         *    {
         *      "name": "EVENT_NAME",
         *      "payload": {},
         *      "sourceEventRule": {
         *          "payload": [
         *            {
         *                "path": "control.id",
         *                "operator": "eq",
         *                "value": "CONTROL_ID"
         *            },
         *          ]
         *      },
         *      "rules": []
         *    },
         *   ]
         * }
         * ```
         */
        const dispatch = () =>
            this._events.dispatch({
                name: 'type:change',
                payload: {
                    id: emitData.control.id,
                    ...emitData,
                },
            });

        dispatch();
    }

    keyUp(...args) {
        const emitData = this.createEmitData(args);
        this.keyupped.emit(emitData);
    }

    submit(...args) {
        const emitData = this.createEmitData(args);

        emitData.form.valid ? this.submitted.emit(emitData) : noop();
    }

    load(...args) {
        const emitData = this.createEmitData(args);
        const dispatch = () => this._events.dispatch({
            name: 'type:load',
            payload: {
                id: emitData.control.id,
                ...emitData
            }
        });
        dispatch();
    }

    search(...args) {
        const emitData = this.createEmitData(args);
        this.searching.emit(emitData);
    }

    reset(...args) {
        const emitData = this.createEmitData(args);
        this.resetted.emit(emitData);
    }

    open() {
        this.opened.emit();
    }

    close() {
        this.closed.emit();
    }

    private _eventBus: IqEventBusBrokerService = inject(
        IqEventBusBrokerService,
    );

    private createEmitData(args: any[] = []): { [key: string]: any } {
        const templateProps: Record<any, any> =
            args?.reduce(
                (
                    currentProps: Record<any, number>,
                    key: any,
                    index: number,
                ) => {
                    return index % 2 === 0
                        ? {
                              ...currentProps,
                              [key]: args?.[index + 1] ?? null,
                          }
                        : currentProps;
                },
                {},
            ) ?? {};
        const formCacheData = this._status.getFormCache(
            templateProps.control.id + '-form',
        );

        const emitData = {
            form: {
                ...this.fg.value,
                valid: this.fg.valid,
                errors: this.fg.errors,
            },
            formCache: formCacheData,
            ...templateProps,
        };
        return emitData;
    }
}
